import type { IntlShape } from 'react-intl-next';
import uuid from 'uuid';

// eslint-disable-next-line import/no-extraneous-dependencies
import type { ADFEntity, ADFEntityMark } from '@atlaskit/adf-utils/types';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { type ContextualToolbar } from '@atlaskit/editor-common/extensions';

import { i18n } from '../messages';
import type { ParseNumber } from '../types';

import { fireToolbarButtonAnalyticsEvent } from './analytics';
import { buildChartADF } from './chartADF';

type TableToolbar = {
	intl: IntlShape;
	parseNumber: ParseNumber;
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
	onInsertButton?: (sourceNode: any) => void;
	featureFlags?: { [flag: string]: any };
};

const globalChartIconComponent = () => import('../ui/icons/GlobalChartIcon');

export const tableToolbar = ({
	intl,
	parseNumber,
	createAnalyticsEvent,
	onInsertButton,
}: TableToolbar): ContextualToolbar => {
	return {
		context: {
			type: 'node',
			nodeType: 'table',
		},
		toolbarItems: (adf: any, _api: any) => {
			// CEMS-2157: waiting on CEMS-2226 and CEMS-2227
			// const disabled = !tableHasNumbers(adf, parseNumber);
			const disabled = false;

			return [
				{
					key: 'insert-chart',
					display: 'icon' as const,
					disabled,

					icon: globalChartIconComponent,

					label: intl.formatMessage(i18n.insertChartTooltip),
					tooltip: intl.formatMessage(i18n.insertChartTooltip),

					action: async (adf: any, api: any) => {
						const localId: string = adf.attrs?.localId || '';
						let sourceId = localId;

						const existingFragmentMark = adf.marks?.find(
							(m: ADFEntityMark) => m.type === 'fragment',
						);

						sourceId = existingFragmentMark?.attrs?.localId || uuid();

						if (!existingFragmentMark) {
							api.doc.update(localId, ({ attrs, marks }: Pick<ADFEntity, 'attrs' | 'marks'>) => ({
								attrs,
								marks: (marks ?? []).concat({
									type: 'fragment',
									attrs: { localId: sourceId },
								}),
							}));
						}

						const chartADF = buildChartADF({
							adf,
							intl,
							parseNumber,
							sourceId,
						});
						api.doc.insertAfter(localId, chartADF, {
							allowSelectionToNewNode: true,
						});
						fireToolbarButtonAnalyticsEvent(adf, createAnalyticsEvent);
						if (onInsertButton) {
							onInsertButton(adf);
						}
					},
				},
			];
		},
	};
};
